import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { TriggerButton } from "../helpers";
import { View } from "../layout";
import { cssValue } from "../prop-types";
import { useBreakpoints } from "../responsive";
import { buildDataTestProps } from "../utils";
import PopoverController from "./PopoverController";
import { PopoverDefaultProps as defaultProps, CONTENT_PADDING, MAX_WIDTH_LARGE_VIEWPORT, MAX_WIDTH_SMALL_VIEWPORT, MAX_HEIGHT_LARGE_VIEWPORT, MAX_HEIGHT_SMALL_VIEWPORT, MENU_CONTENT_ROLE } from "./constants";
import { PopoverPosition } from "./interfaces";
import { jsx as ___EmotionJSX } from "@emotion/core";

var PopoverWithoutDocs = function PopoverWithoutDocs(_ref) {
  var children = _ref.children,
      triggerText = _ref.triggerText,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? defaultProps.position : _ref$position,
      _ref$shouldFocusOnOpe = _ref.shouldFocusOnOpen,
      shouldFocusOnOpen = _ref$shouldFocusOnOpe === void 0 ? defaultProps.shouldFocusOnOpen : _ref$shouldFocusOnOpe,
      _ref$shouldCloseOnFoc = _ref.shouldCloseOnFocusLeave,
      shouldCloseOnFocusLeave = _ref$shouldCloseOnFoc === void 0 ? defaultProps.shouldCloseOnFocusLeave : _ref$shouldCloseOnFoc,
      maxWidthProp = _ref.maxWidth,
      _ref$triggerButtonPro = _ref.triggerButtonProps,
      triggerButtonProps = _ref$triggerButtonPro === void 0 ? defaultProps.triggerButtonProps : _ref$triggerButtonPro,
      passedContentProps = _ref.contentProps,
      onOpened = _ref.onOpened,
      ariaHasPopup = _ref['aria-haspopup'],
      dataTestId = _ref.dataTestId;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var target = useRef(null);
  var viewport = useBreakpoints();
  var isSmallViewport = viewport.matches.s;

  var handleToggle = function handleToggle() {
    setIsOpen(!isOpen);
  };

  var handleClose = function handleClose() {
    setIsOpen(false);
  }; // Having focus trap always deactivate on click outside
  // causes a double event when the trigger is clicked:
  // popover closes because of click outside and reopens
  // because the trigger was clicked.
  // In case when trigger is clicked - close the popover
  // using state, otherwise rely on the focus trap behavior.


  var handleClickOutside = function handleClickOutside(e) {
    if (!shouldCloseOnFocusLeave) {
      return false;
    }

    if (!target.current) {
      return true;
    }

    var eventPath = e.composedPath();
    var clickedTrigger = eventPath.includes(target.current);

    if (!clickedTrigger) {
      return true;
    }

    setIsOpen(false);
    return false;
  };

  var triggerDataTestProps = buildDataTestProps({
    dataTestComponent: 'StencilPopoverTrigger',
    dataTestId: dataTestId
  });
  var contentDataTestProps = buildDataTestProps({
    dataTestComponent: 'StencilPopoverContent',
    dataTestId: dataTestId
  }); // The `role` of the content must match the value of `aria-haspopup`.
  // Special case: `true` or "true" must be treated as "menu"
  // https://www.w3.org/TR/wai-aria/#aria-haspopup

  var contentRole;

  switch (ariaHasPopup) {
    case true:
    case 'true':
    case 'menu':
      contentRole = MENU_CONTENT_ROLE;
      break;

    default:
      contentRole = 'dialog';
  }

  var shouldCreateFocusTrap = shouldFocusOnOpen && contentRole === 'dialog';
  var defaultMaxWidth = isSmallViewport ? MAX_WIDTH_SMALL_VIEWPORT : MAX_WIDTH_LARGE_VIEWPORT;
  var maxWidth = maxWidthProp === null ? undefined : maxWidthProp || defaultMaxWidth;
  var maxHeight = isSmallViewport ? MAX_HEIGHT_SMALL_VIEWPORT : MAX_HEIGHT_LARGE_VIEWPORT;
  var ariaHasPopupAttribute = contentRole === MENU_CONTENT_ROLE ? {
    'aria-haspopup': ariaHasPopup
  } : // 'aria-describedby' should be used by the consumers in triggerButtonProps.
  {};
  var triggerPatternProps = shouldCreateFocusTrap ? // Dialog pattern: https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal.
  {
    shouldSetAriaExpanded: false
  } : // Disclosure button pattern: https://www.w3.org/TR/wai-aria-practices-1.2/#disclosure.
  // Adding aria-haspopup for the menu button.
  _objectSpread(_objectSpread({}, ariaHasPopupAttribute), {}, {
    'aria-expanded': isOpen,
    shouldSetAriaExpanded: true
  });
  var contentPatternProps = shouldFocusOnOpen ? {
    role: contentRole,
    'aria-modal': contentRole === MENU_CONTENT_ROLE ? undefined : true,
    tabIndex: contentRole === MENU_CONTENT_ROLE ? undefined : -1
  } : {};

  var contentProps = _objectSpread(_objectSpread(_objectSpread(_objectSpread({
    padding: CONTENT_PADDING,
    maxWidth: maxWidth,
    maxHeight: maxHeight,
    overflow: 'auto'
  }, passedContentProps), contentPatternProps), contentDataTestProps), {}, {
    children: children({
      close: handleClose
    })
  });

  var initialFocusRef = useRef(null);
  return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(TriggerButton, _extends({}, triggerPatternProps, triggerButtonProps, {
    onClick: handleToggle,
    isExpanded: isOpen,
    ref: target
  }, triggerDataTestProps), triggerText), ___EmotionJSX(PopoverController, {
    target: target,
    close: handleClose,
    onOpened: onOpened,
    isOpen: isOpen,
    position: position,
    shouldFocusOnOpen: shouldFocusOnOpen,
    shouldCloseOnFocusLeave: shouldCreateFocusTrap ? false : shouldCloseOnFocusLeave,
    dataTestId: dataTestId
  }, shouldCreateFocusTrap ? ___EmotionJSX(FocusTrap, {
    focusTrapOptions: {
      delayInitialFocus: false,
      initialFocus: function initialFocus() {
        return initialFocusRef.current;
      },
      fallbackFocus: function fallbackFocus() {
        return ___EmotionJSX("div", {
          tabIndex: -1
        });
      },
      escapeDeactivates: true,
      clickOutsideDeactivates: handleClickOutside,
      onDeactivate: handleClose,
      preventScroll: true
    }
  }, ___EmotionJSX("div", null, ___EmotionJSX(View, _extends({
    ref: initialFocusRef
  }, contentProps)))) : ___EmotionJSX(View, contentProps)));
};

var Popover = describe(PopoverWithoutDocs).description('Displays a popover attached to a trigger button.');
Popover.propTypes = {
  children: PropTypes.func.isRequired.description('Function which returns content to be displayed in the popover.').format("({\n  close: () => void\n}) => React.ReactNode"),
  triggerText: PropTypes.string.isRequired.description('Text to display in the trigger button.'),
  position: PropTypes.oneOf(Object.values(PopoverPosition)).description('The position of the popover relative to the trigger button.'),
  shouldFocusOnOpen: PropTypes.bool.description('Whether the popover body should gain focus when opened.'),
  shouldCloseOnFocusLeave: PropTypes.bool.description('Whether the popover should close when focus moves outside its body.'),
  onOpened: PropTypes.func.description('Function called once the popover has finished opening.').format('() => void'),
  maxWidth: cssValue().description("Sets the CSS 'max-width' property on the popover content."),
  triggerButtonProps: PropTypes.object.description('Props to pass to the trigger button.'),
  contentProps: PropTypes.object.description('Props to pass to the content container.'),
  'aria-haspopup': PropTypes.string.description("Sets the 'aria-haspopup' property on the trigger button."),
  dataTestId: PropTypes.string.description("Sets the 'data-test-id' in the DOM, for testing purposes.")
};
Popover.defaultProps = _objectSpread({}, defaultProps);
Popover.displayName = 'Popover';
export default Popover;